import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Dropdown, Menu } from 'components/Dropdown';
import { Button } from 'components/Form';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { CLASS_DISABLED_REASON } from 'constants/logic';
import { schedulePropTypes } from 'constants/propTypes';
import { getClasspassSpotsAllocationRange } from 'helpers/classpass-spots-allocation';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';
import { selectActiveVenueId } from 'selectors/general';

import { ENABLE_SCHEDULE_SOURCE, enableSchedule } from '../YieldManagement.saga';
import { getInitialEnableClasspassSpots } from './ScheduleDetailV2.helpers';
import messages from './ScheduleDetailV2.messages';
import './DisabledClassCard.scss';

const { block, element } = bem('disabled-class-card');

const PRICING_OPTIONS_HELP_URL = 'https://support.mindbodyonline.com/s/article/203253823-Pricing-options-memberships';

function getBodyMessage({ isPricingOptionIssue, recommendedSpots, smartspotEnabled }) {
  if (isPricingOptionIssue) {
    return messages.disabledClassBodyPricingOption;
  }
  if (smartspotEnabled) {
    return messages.disabledClassBodySmartspot;
  }
  return recommendedSpots ? messages.disabledClassBodyWithEstimate : messages.disabledClassBody;
}

const pricingOptionsLink = text => (
  <a href={PRICING_OPTIONS_HELP_URL} target="_blank" rel="noopener noreferrer">
    {text}
  </a>
);

const SpotsPillToggle = ({ disabled, isOpen, label, toggleMenu, value }) => (
  <button aria-label={label} className={element('spots-toggle')} disabled={disabled} onClick={toggleMenu} type="button">
    {value}
    <SvgArrow direction={isOpen ? 'up' : 'down'} theme={disabled ? 'disabled' : 'dark'} />
  </button>
);

SpotsPillToggle.propTypes = {
  disabled: PropTypes.bool,
  isOpen: PropTypes.bool,
  label: PropTypes.string,
  toggleMenu: PropTypes.func,
  value: PropTypes.number,
};

const DisabledClassCard = ({ schedule }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const venueId = useSelector(selectActiveVenueId);
  const [classpassSpots, setClasspassSpots] = useState(() => getInitialEnableClasspassSpots(schedule));
  const [isEnabling, setIsEnabling] = useState(false);

  const { scheduleClass, scheduleId, smartspotEnabled, spots } = schedule;
  const recommendedSpots = spots?.smartspotRecommendedSpots;
  const isPricingOptionIssue = scheduleClass?.disabledReason === CLASS_DISABLED_REASON.INVALID_PRICING_OPTION;
  const spotsToEnable = smartspotEnabled ? null : classpassSpots;

  const handleEnable = () => {
    setIsEnabling(true);
    Segment.event('Schedule Enabled', { venueId, scheduleId, spotsAdded: spotsToEnable });
    dispatch(
      enableSchedule(
        venueId,
        schedule,
        scheduleClass?.classDisabled,
        smartspotEnabled,
        spotsToEnable,
        () => setIsEnabling(false),
        ENABLE_SCHEDULE_SOURCE.DISABLED_CLASS_CARD
      )
    );
  };

  return (
    <div className={block()} data-cypress="schedule-detail-v2-disabled-class">
      <div className={element('copy')}>
        <Message as="p" className={element('title')} message={messages.disabledClassTitle} />
        <Message
          as="p"
          className={element('body')}
          message={getBodyMessage({ isPricingOptionIssue, recommendedSpots, smartspotEnabled })}
          values={{ a: pricingOptionsLink, count: recommendedSpots }}
        />
      </div>
      {!smartspotEnabled && (
        <div className={element('spots')}>
          <Message className={element('spots-label')} message={messages.disabledClassSpotsLabel} />
          <Dropdown
            attachment="center"
            closeOnScroll
            Content={Menu}
            dataCypress="schedule-detail-v2-disabled-class-spots"
            disabled={isEnabling}
            isSelected={option => option === classpassSpots}
            items={getClasspassSpotsAllocationRange(schedule)}
            label={intl.formatMessage(messages.disabledClassSpotsLabel)}
            onSelect={setClasspassSpots}
            Toggle={SpotsPillToggle}
            value={classpassSpots}
          />
        </div>
      )}
      <Button className={element('enable', 'bt--sm', 'bt--text-sm')} disabled={isEnabling} onClick={handleEnable}>
        <Message message={messages.disabledClassEnable} />
      </Button>
    </div>
  );
};

DisabledClassCard.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes).isRequired,
};

export default DisabledClassCard;
